// Frases do carregamento de filme e série (o "Carregando…" virou uma frase de sofá).
//
// O desenho (ux-tv + redator, 10/10/2026, com a rodada de ajustes dos gates):
//   - uma linha só, abaixo do spinner. Nos primeiros PHRASE_START_MS só o spinner
//     (carga rápida não pisca texto); depois uma frase a cada PHRASE_INTERVAL_MS;
//   - frases "de PROMESSA" ("Pipoca quase pronta", "Abrindo as cortinas"…) só nas
//     PROMISE_MAX_POSITION primeiras posições (até ~9,5 s). Da 3ª em diante só as "de
//     ATMOSFERA": aos 40 s, "quase pronta" já vira "mentira, travou";
//   - o baralho depende da SUPERFÍCIE: no toque (mobile/web estreita) saem a do
//     controle remoto e a do celular (quem segura o celular está nele);
//   - aos PHRASE_STALL_MS a frase dá lugar a um aviso honesto ("Está demorando mais
//     que o normal" + como sair) e o relógio morre.
//
// Tudo aqui é PURO (sem React, sem timer): o componente `LoadingPhrase` só executa a
// linha do tempo de `nextPhraseTick` e pede a próxima carta ao baralho.

/** Quantas frases existem em `player.loadingPhrases` (p01..p12) nos 3 locales. */
export const LOADING_PHRASE_COUNT = 12

/** Até aqui só o spinner. Aos 1500 ms entra a 1ª frase. */
export const PHRASE_START_MS = 1500

/** Intervalo entre uma frase e a próxima. */
export const PHRASE_INTERVAL_MS = 4000

/** Aos 60 s a frase dá lugar ao aviso de demora e o timer não é rearmado. */
export const PHRASE_STALL_MS = 60_000

/** Frases de promessa só saem nas posições 1..PROMISE_MAX_POSITION de cada carga. */
export const PROMISE_MAX_POSITION = 2

/**
 * As chaves, escritas por extenso (sem `padStart`, que não existe no Chr38, e para
 * que uma varredura estática de chaves usadas as encontre).
 */
export const LOADING_PHRASE_KEYS: readonly string[] = [
  'player.loadingPhrases.p01',
  'player.loadingPhrases.p02',
  'player.loadingPhrases.p03',
  'player.loadingPhrases.p04',
  'player.loadingPhrases.p05',
  'player.loadingPhrases.p06',
  'player.loadingPhrases.p07',
  'player.loadingPhrases.p08',
  'player.loadingPhrases.p09',
  'player.loadingPhrases.p10',
  'player.loadingPhrases.p11',
  'player.loadingPhrases.p12',
]

/** Índices (0-based) das frases de PROMESSA: p01, p02, p08, p09. */
export const PROMISE_PHRASES: readonly number[] = [0, 1, 7, 8]

/** Índices que NÃO saem na superfície de toque: p03 (controle remoto), p07 (celular). */
export const TOUCH_EXCLUDED_PHRASES: readonly number[] = [2, 6]

/** 'tv' = superfície TV (TV real e web larga); 'touch' = superfície mobile. */
export type PhraseSurface = 'tv' | 'touch'

/** Chaves do aviso de demora (aos PHRASE_STALL_MS). */
export const LOADING_STALLED_KEYS = {
  title: 'player.loadingStalled.title',
  hintTv: 'player.loadingStalled.hintTv',
  hintTouch: 'player.loadingStalled.hintTouch',
} as const

/** Frases que a superfície pode mostrar (qualquer posição). */
export function allowedPhrases(surface: PhraseSurface): number[] {
  const out: number[] = []
  for (let i = 0; i < LOADING_PHRASE_COUNT; i++) {
    if (surface === 'touch' && TOUCH_EXCLUDED_PHRASES.indexOf(i) !== -1) continue
    out.push(i)
  }
  return out
}

/** Frases de ATMOSFERA da superfície (as únicas da 3ª posição em diante). */
export function atmospherePhrases(surface: PhraseSurface): number[] {
  return allowedPhrases(surface).filter((i) => PROMISE_PHRASES.indexOf(i) === -1)
}

/** Próximo passo da linha do tempo de uma carga. `null` = acabou. */
export type PhraseTick = { at: number; kind: 'show' | 'stall' }

/**
 * Linha do tempo de UMA carga, medida desde a montagem: dado o instante do passo que
 * acabou de acontecer (`elapsedMs`; 0 = montou), devolve o próximo.
 *
 *   0 → show@1500 → show@5500 → … → show@57500 → stall@60000 → null
 */
export function nextPhraseTick(elapsedMs: number): PhraseTick | null {
  if (elapsedMs < PHRASE_START_MS) return { at: PHRASE_START_MS, kind: 'show' }
  if (elapsedMs >= PHRASE_STALL_MS) return null
  const next = elapsedMs + PHRASE_INTERVAL_MS
  if (next >= PHRASE_STALL_MS) return { at: PHRASE_STALL_MS, kind: 'stall' }
  return { at: next, kind: 'show' }
}

/** Fisher-Yates sobre uma cópia de `pool`. `avoidFirst` nunca fica na 1ª posição. */
function shuffled(pool: readonly number[], random: () => number, avoidFirst: number | null): number[] {
  const order = pool.slice()
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    const tmp = order[i] as number
    order[i] = order[j] as number
    order[j] = tmp
  }
  if (order.length > 1 && avoidFirst !== null && order[0] === avoidFirst) {
    // Troca a 1ª com uma das outras posições (sorteada), sem viciar a ordem.
    const j = 1 + Math.floor(random() * (order.length - 1))
    const tmp = order[0] as number
    order[0] = order[j] as number
    order[j] = tmp
  }
  return order
}

export interface PhraseDeck {
  /** Próxima carta do baralho (um elemento de `pool`). Nunca repete a anterior. */
  next(): number
}

/**
 * Baralho sobre `pool`: percorre uma ordem embaralhada sem repetir e reembaralha ao
 * esgotar, com a nova 1ª carta diferente da última da volta anterior (nunca a mesma
 * carta duas vezes seguidas). `avoidFirst` é a carta proibida na 1ª tirada.
 * `firstRoundExclude`: cartas que ficam de fora só da 1ª volta (já foram vistas).
 */
export function createPhraseDeck(
  pool: readonly number[],
  random: () => number = Math.random,
  avoidFirst: number | null = null,
  firstRoundExclude: readonly number[] = [],
): PhraseDeck {
  const firstPool = pool.filter((c) => firstRoundExclude.indexOf(c) === -1)
  let order = shuffled(firstPool.length > 0 ? firstPool : pool, random, avoidFirst)
  let pos = 0
  let last: number | null = null
  return {
    next(): number {
      if (pos >= order.length) {
        order = shuffled(pool, random, last)
        pos = 0
      }
      const card = order[pos] as number
      pos += 1
      last = card
      return card
    },
  }
}

// Memória entre cargas (variável de módulo, sem storage): a 1ª frase MOSTRADA na
// carga anterior. Só é gravada quando a 1ª frase aparece de fato, então uma carga que
// terminou antes de 1500 ms não "gasta" a frase.
let lastFirstShown: number | null = null

/**
 * Baralho de UMA carga, na superfície dada. Cada `next()` devolve a chave i18n.
 *   - posições 1..PROMISE_MAX_POSITION: qualquer frase da superfície, sem repetir; a
 *     1ª nunca repete a 1ª mostrada na carga anterior;
 *   - da posição seguinte em diante: só ATMOSFERA, sem repetir até esgotar (a 1ª
 *     volta pula as de atmosfera já vistas nas primeiras posições) e nunca a mesma
 *     duas vezes seguidas.
 */
export function createLoadPhraseDeck(
  surface: PhraseSurface,
  random: () => number = Math.random,
): { next(): string } {
  const opening = createPhraseDeck(allowedPhrases(surface), random, lastFirstShown)
  let atmosphere: PhraseDeck | null = null
  const shown: number[] = []
  return {
    next(): string {
      let card: number
      if (shown.length < PROMISE_MAX_POSITION) {
        card = opening.next()
      } else {
        if (atmosphere === null) {
          const last = shown[shown.length - 1] ?? null
          atmosphere = createPhraseDeck(atmospherePhrases(surface), random, last, shown)
        }
        card = atmosphere.next()
      }
      if (shown.length === 0) lastFirstShown = card
      shown.push(card)
      return LOADING_PHRASE_KEYS[card] as string
    },
  }
}

/** Só para teste: zera a memória entre cargas. */
export function resetLoadingPhraseMemoryForTests(): void {
  lastFirstShown = null
}
